"use client";

import React, { useRef, useEffect, useState } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { AboutPageData } from "@/actions/about.actions";
import Footer from "@/components/global/footer";
import FittedText from "@/components/global/fitted-text";
import ScrollPrompt from "@/components/global/scroll-prompt";

gsap.registerPlugin(ScrollTrigger);

interface AboutClientProps {
  data: AboutPageData;
  extinctLogoUrl?: string;
}

const TOTAL_BARS = 10;
const TOTAL_BARS_MOBILE = 4;
const SPECIAL_BAR_INDEX = 6;
const SPECIAL_BAR_INDEX_MOBILE = 2;

function getImageUrl(
  image: string | { url: string } | { public_id: string } | undefined,
): string | null {
  if (!image) return null;
  if (typeof image === "string") return image;
  if ("url" in image) return image.url;
  return null;
}

export default function AboutClient({
  data,
  extinctLogoUrl,
}: AboutClientProps) {
  // --- REFS ---
  const containerRef = useRef<HTMLDivElement>(null);
  const pinnedWrapperRef = useRef<HTMLDivElement>(null);
  const barSectionContainerRef = useRef<HTMLDivElement>(null);
  const section1ContainerRef = useRef<HTMLDivElement>(null);

  const mobileBarsRef = useRef<(HTMLDivElement | null)[]>([]);
  const desktopBarsRef = useRef<(HTMLDivElement | null)[]>([]);

  const mobileImageRef = useRef<HTMLDivElement>(null);
  const desktopImageRef = useRef<HTMLDivElement>(null);

  const textLinesRef = useRef<(HTMLSpanElement | null)[]>([]);
  const counterRef = useRef<HTMLDivElement>(null);
  const counterHundredsRef = useRef<HTMLDivElement>(null);
  const counterTensRef = useRef<HTMLDivElement>(null);
  const counterOnesRef = useRef<HTMLDivElement>(null);

  // Section 1 Refs (Intro)
  const section1MessageImageRef = useRef<HTMLDivElement>(null);
  const section1TextRef = useRef<HTMLDivElement>(null);
  const section1PortraitRef = useRef<HTMLDivElement>(null);
  // NEW REF: Specifically for the mobile layout doodles
  const section1MobileDoodlesRef = useRef<HTMLDivElement>(null);
  // NEW REF: Specifically for the mobile layout portrait
  const section1MobilePortraitRef = useRef<HTMLDivElement>(null);
  // Refs for arrow and Stewart Tavener text doodles (desktop)
  const section1ArrowDoodleRef = useRef<HTMLDivElement>(null);
  const section1StewartDoodleRef = useRef<HTMLDivElement>(null);
  // Ref for desktop doodles container (separate from portrait for independent animation)
  const section1DoodlesDesktopRef = useRef<HTMLDivElement>(null);

  // Refs for Stewart doodle scroll tracking
  const stewartScrollListenerRef = useRef<(() => void) | null>(null);
  // Ref for intro kill function (for cleanup)
  const killIntroOnScrollRef = useRef<(() => void) | null>(null);

  // Needle ref for scroll animation
  const needleRef = useRef<HTMLDivElement>(null);

  // Refs for sweep section animations
  const sweepHeadingRef = useRef<HTMLDivElement>(null);
  const sweepBodyRef = useRef<HTMLDivElement>(null);
  const sweepDoodleRef = useRef<HTMLDivElement>(null);

  // Refs for sound recording section
  const soundRecordingHeadingRef = useRef<HTMLDivElement>(null);
  const soundRecordingTextRef = useRef<HTMLDivElement>(null);
  const soundRecordingDoodleRef = useRef<HTMLDivElement>(null);

  const [isReady, setIsReady] = useState(false);
  const [spacerHeight, setSpacerHeight] = useState("100vh");
  const [isMobileView, setIsMobileView] = useState(false);
  const [showScrollPrompt, setShowScrollPrompt] = useState(false);

  // Set mobile view state on mount
  useEffect(() => {
    setIsMobileView(window.innerWidth < 768);
  }, []);

  // Needle scroll animation
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let lastTime = Date.now();
    let lastTweenTime = 0;
    let scrollSpeed = 0;
    let animationFrameId: number;

    const updateNeedleRotation = () => {
      if (!needleRef.current) return;

      const currentTime = Date.now();
      const currentScrollY = window.scrollY;
      const timeDelta = currentTime - lastTime;
      const scrollDelta = Math.abs(currentScrollY - lastScrollY);

      // Calculate scroll speed with heavy smoothing
      if (timeDelta > 0) {
        const newSpeed = scrollDelta / timeDelta;
        // Heavy smoothing - only 8% of new input per frame
        scrollSpeed = scrollSpeed * 0.92 + newSpeed * 0.08;
      }

      // Only update tween every 50ms to reduce churn
      if (currentTime - lastTweenTime > 50) {
        // Map scroll speed to rotation (-30 to 60 degrees)
        // Speed of 0 = -30 degrees, speed of 4+ = 60 degrees
        const maxSpeed = 2.5;
        const normalizedSpeed = Math.min(scrollSpeed / maxSpeed, 1);
        const targetRotation = -30 + normalizedSpeed * 90;

        // Use GSAP to smoothly animate to target rotation
        gsap.to(needleRef.current, {
          xPercent: -50,
          rotation: targetRotation,
          duration: 0.6,
          ease: "power3.out",
          overwrite: true,
        });

        lastTweenTime = currentTime;
      }

      lastScrollY = currentScrollY;
      lastTime = currentTime;

      // Faster decay when not actively scrolling
      scrollSpeed *= 0.9;

      animationFrameId = requestAnimationFrame(updateNeedleRotation);
    };

    const handleScroll = () => {
      if (!animationFrameId) {
        animationFrameId = requestAnimationFrame(updateNeedleRotation);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    animationFrameId = requestAnimationFrame(updateNeedleRotation);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  useGSAP(
    () => {
      setIsReady(true);

      const isMobile = window.innerWidth < 768;
      const activeBars = isMobile
        ? mobileBarsRef.current
        : desktopBarsRef.current;
      const activeImage = isMobile
        ? mobileImageRef.current
        : desktopImageRef.current;
      const currentTotalBars = isMobile ? TOTAL_BARS_MOBILE : TOTAL_BARS;
      const currentSpecialBarIndex = isMobile
        ? SPECIAL_BAR_INDEX_MOBILE
        : SPECIAL_BAR_INDEX;

      // --- 1. INITIAL SETUPS ---

      // Force Intro Section hidden immediately
      if (section1ContainerRef.current) {
        gsap.set(section1ContainerRef.current, { autoAlpha: 0 });
      }

      // Hide Hero Text
      if (textLinesRef.current.length > 0) {
        gsap.set(textLinesRef.current, {
          opacity: 0,
          x: -50,
          visibility: "hidden",
        });
      }

      // Initialize bars hidden
      [mobileBarsRef.current, desktopBarsRef.current].forEach((refArray) => {
        if (refArray.length > 0) {
          gsap.set(refArray, { scaleY: 0, transformOrigin: "top", opacity: 0 });
        }
      });

      // Set initial transform state on images via GSAP for consistency
      // Values adjusted to position mic centrally in the mask window
      [mobileImageRef.current, desktopImageRef.current].forEach((container) => {
        if (container) {
          const img = container.querySelector("img");
          if (img) gsap.set(img, { scale: 2.5, xPercent: 65, yPercent: -55 });
        }
      });

      // Reset Scarcity Items
      const scarcityItems = gsap.utils.toArray<HTMLElement>(".scarcity-item");
      if (scarcityItems.length > 0) {
        gsap.set(scarcityItems, { y: 0, opacity: 1, clearProps: "all" });
      }

      // Prepare Intro Section Elements
      if (section1MessageImageRef.current)
        gsap.set(section1MessageImageRef.current, { opacity: 0, x: -50 });
      if (section1TextRef.current)
        gsap.set(section1TextRef.current, { opacity: 0 });
      if (section1PortraitRef.current)
        gsap.set(section1PortraitRef.current, { opacity: 0 });

      // FIX: Ensure Mobile Doodles start hidden
      if (section1MobileDoodlesRef.current)
        gsap.set(section1MobileDoodlesRef.current, { opacity: 0 });

      // FIX: Ensure Mobile Portrait starts hidden
      if (section1MobilePortraitRef.current)
        gsap.set(section1MobilePortraitRef.current, { opacity: 0 });

      // Ensure desktop doodles container starts hidden
      if (section1DoodlesDesktopRef.current)
        gsap.set(section1DoodlesDesktopRef.current, { opacity: 0 });

      // Reset Stewart doodle scroll state
      if (section1StewartDoodleRef.current) {
        gsap.set(section1StewartDoodleRef.current, { opacity: 1 });
      }
      if (section1ArrowDoodleRef.current) {
        gsap.set(section1ArrowDoodleRef.current, { opacity: 1 });
      }

      const timer = setTimeout(() => {
        if (pinnedWrapperRef.current && section1ContainerRef.current) {
          setSpacerHeight(`${section1ContainerRef.current.offsetHeight}px`);
        }

        // --- 2. INTRO TIMELINE ---
        const introTl = gsap.timeline({
          onComplete: () => setShowScrollPrompt(true),
        });
        const barDuration = 2;
        const staggerAmount = 0.5;

        const normalBars = activeBars.filter(
          (_, index) =>
            index !== currentSpecialBarIndex && index < currentTotalBars,
        );

        introTl.fromTo(
          normalBars,
          { scaleY: 0, opacity: 0 },
          {
            scaleY: 1,
            opacity: 1,
            duration: barDuration,
            stagger: { amount: staggerAmount, from: "start" },
            ease: "power3.out",
          },
          0,
        );

        introTl.fromTo(
          textLinesRef.current,
          { opacity: 0, x: -50, visibility: "hidden" },
          {
            opacity: 1,
            x: 0,
            visibility: "visible",
            duration: 1.8,
            stagger: 0.1,
            ease: "power3.out",
          },
          0.5,
        );

        if (activeImage && activeBars[currentSpecialBarIndex]) {
          const staggerDelay =
            (currentSpecialBarIndex / (currentTotalBars - 1)) * staggerAmount;
          const specialBar = activeBars[currentSpecialBarIndex];
          const progressObj = { value: 0 };

          introTl.to(
            progressObj,
            {
              value: 1,
              duration: barDuration,
              ease: "power3.out",
              onUpdate: () => {
                const progress = progressObj.value;
                const barProgress = progress <= 0.7 ? progress / 0.7 : 1;
                const imageProgress =
                  progress > 0.7 ? (progress - 0.7) / 0.3 : 0;

                if (specialBar)
                  gsap.set(specialBar, {
                    scaleY: barProgress,
                    opacity: progress,
                  });

                gsap.set(activeImage, {
                  clipPath: `inset(0% 0% ${100 - imageProgress * 100}% 0%)`,
                  autoAlpha: imageProgress > 0 ? 1 : 0,
                });
              },
            },
            staggerDelay,
          );
        }

        // --- 3. SCROLL TIMELINE ---
        let introKilled = false;

        // Kill intro on user scroll attempt (wheel or touch)
        killIntroOnScrollRef.current = () => {
          if (!introKilled) {
            introKilled = true;
            introTl.progress(1).kill();
            // Ensure elements are in final state
            gsap.set(activeBars, { scaleY: 1, opacity: 1 });
            gsap.set(textLinesRef.current, { opacity: 1, x: 0, visibility: "visible" });
            if (activeImage) {
              gsap.set(activeImage, { clipPath: "inset(0% 0% 0% 0%)", autoAlpha: 1 });
            }
          }
          if (killIntroOnScrollRef.current) {
            window.removeEventListener("wheel", killIntroOnScrollRef.current);
            window.removeEventListener("touchstart", killIntroOnScrollRef.current);
          }
        };

        window.addEventListener("wheel", killIntroOnScrollRef.current, { passive: true, once: true });
        window.addEventListener("touchstart", killIntroOnScrollRef.current, { passive: true, once: true });

        const scrollTl = gsap.timeline({
          scrollTrigger: {
            trigger: pinnedWrapperRef.current,
            start: "top top",
            end: isMobile ? "+=2800" : "+=6200",
            scrub: isMobile ? 0.5 : true,
            pin: true,
            pinSpacing: true,
            anticipatePin: 1,
            onLeave: () => {
              // Arrow and Stewart text animations disabled - show immediately with container
              /*
              // Arrow animation disabled
              if (section1ArrowDoodleRef.current) {
                gsap.set(section1ArrowDoodleRef.current, {
                  transformOrigin: "200% 40%",
                });

                gsap.to(section1ArrowDoodleRef.current, {
                  opacity: 1,
                  duration: 0.4,
                  ease: "power2.out",
                });

                gsap.fromTo(
                  section1ArrowDoodleRef.current,
                  { rotation: 0 },
                  {
                    rotation: -8,
                    duration: 2 / 6,
                    ease: "power1.inOut",
                    repeat: 3,
                    yoyo: true,
                  },
                );
              }
              */

              // Stewart text scroll tracking disabled
              /*
              if (section1StewartDoodleRef.current) {
                stewartScrollStartRef.current = window.scrollY;
                stewartVisibleRef.current = false;

                // Remove any existing listener
                if (stewartScrollListenerRef.current) {
                  window.removeEventListener("scroll", stewartScrollListenerRef.current);
                }

                const handleScroll = () => {
                  const scrolled = window.scrollY - stewartScrollStartRef.current;

                  if (scrolled >= 400 && !stewartVisibleRef.current) {
                    // Scrolled 400px past unpin - show Stewart text
                    stewartVisibleRef.current = true;
                    gsap.to(section1StewartDoodleRef.current, {
                      opacity: 1,
                      duration: 0.4,
                      ease: "power2.out",
                    });
                  } else if (scrolled < 400 && stewartVisibleRef.current) {
                    // Scrolled back before 400px threshold - hide Stewart text
                    stewartVisibleRef.current = false;
                    gsap.to(section1StewartDoodleRef.current, {
                      opacity: 0,
                      duration: 0.2,
                      ease: "power2.in",
                    });
                  }
                };

                stewartScrollListenerRef.current = handleScroll;
                window.addEventListener("scroll", handleScroll, { passive: true });
              }
              */
            },
            onEnterBack: () => {
              // Hide arrow animation disabled
              /*
              if (section1ArrowDoodleRef.current) {
                gsap.to(section1ArrowDoodleRef.current, {
                  opacity: 0,
                  rotation: 0, // Reset rotation
                  duration: 0.2,
                  ease: "power2.in",
                  overwrite: "auto",
                });
              }
              */

              // Clean up Stewart scroll listener and hide Stewart text
              if (stewartScrollListenerRef.current) {
                window.removeEventListener("scroll", stewartScrollListenerRef.current);
                stewartScrollListenerRef.current = null;
              }
              /*
              if (section1StewartDoodleRef.current) {
                gsap.to(section1StewartDoodleRef.current, {
                  opacity: 0,
                  duration: 0.2,
                  ease: "power2.in",
                  overwrite: "auto",
                });
                stewartVisibleRef.current = false;
              }
              */
            },
          },
        });

        if (activeImage) {
          const rect = activeImage.getBoundingClientRect();
          const specialBar = activeBars[currentSpecialBarIndex];

          // Capture mic's starting and ending screen positions for smooth animation
          // Mic is at 40% from left of image. With initial transform (scale 2.5, xPercent 65),
          // it appears at 187.5% from left edge of container.
          const startMicScreenX = rect.left + rect.width * 1.875;
          const endMicScreenX = window.innerWidth * 0.4;

          scrollTl.set(
            activeImage,
            {
              position: "fixed",
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
              bottom: "auto",
              right: "auto",
              zIndex: 100,
              opacity: 1,
            },
            0,
          );

          // REVEAL INTRO SECTION UNDERNEATH (Time: 0.25)
          // Using fromTo instead of set so it scrubs backwards properly
          if (section1ContainerRef.current) {
            scrollTl.fromTo(
              section1ContainerRef.current,
              { autoAlpha: 0 },
              { autoAlpha: 1, duration: 0.01 },
              0.25,
            );
          }

          scrollTl.to(
            activeImage,
            { top: `+=${50}`, duration: 0.3, ease: "power2.inOut" },
            0,
          );
          if (specialBar)
            scrollTl.to(
              specialBar,
              { y: 50, duration: 0.3, ease: "power2.inOut" },
              0,
            );

          // Bars Recede
          scrollTl.fromTo(
            activeBars,
            { scaleY: 1 },
            {
              scaleY: 0,
              duration: 0.4,
              stagger: { amount: 0.15, from: "end" },
              ease: "power3.in",
            },
            0.3,
          );

          // Text Fades
          scrollTl.fromTo(
            textLinesRef.current,
            { opacity: 1, x: 0 },
            {
              opacity: 0,
              x: -50,
              duration: 0.3,
              stagger: { amount: 0.12, from: "end" },
              ease: "power3.in",
            },
            0.3,
          );

          // Zoom Image
          scrollTl.to(
            activeImage,
            {
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              opacity: 0.3,
              duration: 0.48,
              ease: "power2.inOut",
            },
            0.7,
          );

          // Scale down the img element inside to show full image and reset position
          // Uses onUpdate to calculate x dynamically for consistent leftward mic movement
          const imgElement = activeImage.querySelector("img");
          if (imgElement) {
            const startScale = 2.5;
            const endScale = 1;
            const startYPercent = -55;
            const endYPercent = 0;

            scrollTl.to(
              { progress: 0 },
              {
                progress: 1,
                duration: 0.48,
                ease: "power2.inOut",
                onUpdate: function () {
                  const p = this.targets()[0].progress;

                  // Calculate current scale and y (these follow the easing curve)
                  const currentScale = startScale + (endScale - startScale) * p;
                  const currentY =
                    startYPercent + (endYPercent - startYPercent) * p;

                  // Calculate target mic screen position (linear interpolation for consistent movement)
                  const targetMicScreenX =
                    startMicScreenX + (endMicScreenX - startMicScreenX) * p;

                  // Get current container position/size
                  const containerRect = activeImage.getBoundingClientRect();

                  // Calculate required xPercent to position mic at targetMicScreenX
                  // Formula: micScreenX = containerCenter + containerWidth * (xPercent/100 - 0.1) * scale
                  // Solving for xPercent: xPercent = ((micScreenX - containerCenter) / (containerWidth * scale) + 0.1) * 100
                  const containerCenter =
                    containerRect.left + containerRect.width / 2;
                  const requiredXPercent =
                    ((targetMicScreenX - containerCenter) /
                      (containerRect.width * currentScale) +
                      0.1) *
                    100;

                  gsap.set(imgElement, {
                    scale: currentScale,
                    xPercent: requiredXPercent,
                    yPercent: currentY,
                  });
                },
              },
              0.7,
            );
          }

          // Counter Logic
          if (counterRef.current && data.totalMicrophones) {
            const totalMics = data.totalMicrophones;
            const counterObj = { value: 0 };

            scrollTl.fromTo(
              counterRef.current,
              { autoAlpha: 0 },
              { autoAlpha: 1, duration: 0.02 },
              1.18,
            );

            scrollTl.to(
              counterObj,
              {
                value: totalMics,
                duration: 0.6,
                ease: "power2.inOut",
                onUpdate: () => {
                  const value = Math.floor(counterObj.value);
                  const hundreds = Math.floor(value / 100);
                  const tens = Math.floor((value % 100) / 10);
                  const ones = value % 10;
                  if (counterHundredsRef.current)
                    counterHundredsRef.current.textContent =
                      hundreds > 0 ? hundreds.toString() : "";
                  if (counterTensRef.current)
                    counterTensRef.current.textContent =
                      hundreds > 0 || tens > 0 ? tens.toString() : "";
                  if (counterOnesRef.current)
                    counterOnesRef.current.textContent = ones.toString();
                },
              },
              1.18,
            );

            scrollTl.to({}, { duration: 0.33 }, 1.78);

            // Step 1: Counter (numbers) fade out FIRST
            scrollTl.to(
              counterRef.current,
              { autoAlpha: 0, duration: 0.15, ease: "power2.inOut" },
              2.11,
            );

            // Step 2: Image shrinks and fades out (after counter + pause)
            scrollTl.to(
              activeImage,
              {
                scale: 0.7,
                autoAlpha: 0,
                duration: 0.4,
                ease: "power2.inOut",
              },
              2.41,
            );

            // 4. FADE IN INTRO CONTENT

            // First: Message Graphic
            if (section1MessageImageRef.current) {
              scrollTl.to(
                section1MessageImageRef.current,
                { opacity: 1, x: 0, duration: 0.22, ease: "power2.out" },
                2.85,
              );
            }

            // Second: Body copy fades in first
            if (section1TextRef.current) {
              scrollTl.to(
                section1TextRef.current,
                { opacity: 1, duration: 0.15, ease: "power2.out" },
                3.1,
              );
            }

            // Third: Portrait fades in (same time as body copy)
            if (section1PortraitRef.current) {
              scrollTl.to(
                section1PortraitRef.current,
                { opacity: 1, duration: 0.15, ease: "power2.out" },
                3.1,
              );
            }

            // Fourth: Desktop doodles container fades in (arrow/stewart inside stay hidden until unpin)
            if (section1DoodlesDesktopRef.current) {
              scrollTl.to(
                section1DoodlesDesktopRef.current,
                { opacity: 1, duration: 0.15, ease: "power2.out" },
                3.1,
              );
            }

            // Mobile doodles fade in during pinned scroll
            if (section1MobileDoodlesRef.current) {
              scrollTl.to(
                section1MobileDoodlesRef.current,
                { opacity: 1, duration: 0.11, ease: "power2.out" },
                3.25,
              );
            }

            // Mobile portrait fades in
            if (section1MobilePortraitRef.current) {
              scrollTl.to(
                section1MobilePortraitRef.current,
                { opacity: 1, duration: 0.11, ease: "power2.out" },
                3.25,
              );
            }
          }
        }
      }, 150);

      // Refresh ScrollTrigger after initialization to prevent layout issues
      const refreshTimer = setTimeout(() => {
        ScrollTrigger.refresh();
      }, 200);

      // Scroll animations for sweep section - delay to ensure elements are ready
      setTimeout(() => {
        // Sweep section - trigger earlier
        if (sweepHeadingRef.current) {
          gsap.fromTo(
            sweepHeadingRef.current,
            { opacity: 0, x: -100 },
            {
              opacity: 1,
              x: 0,
              ease: "none",
              scrollTrigger: {
                trigger: sweepHeadingRef.current,
                start: "top 90%",
                end: "top 40%",
                scrub: 0.5,
              },
            },
          );
        }

        if (sweepBodyRef.current) {
          gsap.fromTo(
            sweepBodyRef.current,
            { opacity: 0, x: 100 },
            {
              opacity: 1,
              x: 0,
              ease: "none",
              scrollTrigger: {
                trigger: sweepBodyRef.current,
                start: "top 90%",
                end: "top 40%",
                scrub: 0.5,
              },
            },
          );
        }

        if (sweepDoodleRef.current) {
          gsap.fromTo(
            sweepDoodleRef.current,
            { opacity: 0, scale: 0.8 },
            {
              opacity: 1,
              scale: 1,
              ease: "none",
              scrollTrigger: {
                trigger: sweepDoodleRef.current,
                start: "top 90%",
                end: "top 40%",
                scrub: 0.5,
              },
            },
          );
        }

        // Sound recording section animations
        if (soundRecordingHeadingRef.current) {
          gsap.fromTo(
            soundRecordingHeadingRef.current,
            { opacity: 0, y: 50 },
            {
              opacity: 1,
              y: 0,
              ease: "none",
              scrollTrigger: {
                trigger: soundRecordingHeadingRef.current,
                start: "top 90%",
                end: "top 40%",
                scrub: 0.5,
              },
            },
          );
        }

        if (soundRecordingTextRef.current) {
          gsap.fromTo(
            soundRecordingTextRef.current,
            { opacity: 0, y: 50 },
            {
              opacity: 1,
              y: 0,
              ease: "none",
              scrollTrigger: {
                trigger: soundRecordingTextRef.current,
                start: "top 90%",
                end: "top 40%",
                scrub: 0.5,
              },
            },
          );
        }

        if (soundRecordingDoodleRef.current) {
          gsap.fromTo(
            soundRecordingDoodleRef.current,
            { opacity: 0, scale: 0.8 },
            {
              opacity: 1,
              scale: 1,
              ease: "none",
              scrollTrigger: {
                trigger: soundRecordingDoodleRef.current,
                start: "top 90%",
                end: "top 40%",
                scrub: 0.5,
              },
            },
          );
        }

        // Scarcity items animation - all drop together with bounce when first item hits 50%
        const scarcityItemsForAnimation =
          gsap.utils.toArray<HTMLElement>(".scarcity-item");
        if (
          scarcityItemsForAnimation.length > 0 &&
          scarcityItemsForAnimation[0]
        ) {
          // Set all items to initial state
          gsap.set(scarcityItemsForAnimation, { opacity: 0, y: -200 });

          // Animate all together with stagger based on first item's trigger
          gsap.to(scarcityItemsForAnimation, {
            opacity: 1,
            y: 0,
            duration: 1.2,
            ease: "bounce.out",
            stagger: 0.1,
            scrollTrigger: {
              trigger: scarcityItemsForAnimation[0],
              start: "top 50%",
              toggleActions: "play none none reverse",
            },
          });
        }

        ScrollTrigger.refresh();
      }, 500);

      return () => {
        clearTimeout(timer);
        clearTimeout(refreshTimer);
        // Clean up scroll detection listeners
        if (killIntroOnScrollRef.current) {
          window.removeEventListener("wheel", killIntroOnScrollRef.current);
          window.removeEventListener("touchstart", killIntroOnScrollRef.current);
          killIntroOnScrollRef.current = null;
        }
        // Clean up Stewart scroll listener
        if (stewartScrollListenerRef.current) {
          window.removeEventListener("scroll", stewartScrollListenerRef.current);
          stewartScrollListenerRef.current = null;
        }
      };
    },
    { scope: containerRef },
  );

  return (
    <main
      className="page-content w-full overflow-x-hidden relative"
      ref={containerRef}
    >
      <div
        ref={pinnedWrapperRef}
        className="relative w-full"
        style={{ opacity: isReady ? 1 : 0 }}
      >
        <div style={{ height: spacerHeight, pointerEvents: "none" }} />

        {/* --- LAYER 2: INTRO SECTION (Revealed Underneath) --- */}
        {data.aboutPageIntroSectionText && (
          <div
            className="absolute top-0 left-0 right-0 w-full py-32 z-10 opacity-0 invisible"
            ref={section1ContainerRef}
          >
            <div className="about-grid-container items-start">
              {/* 1. Heading Image */}
              <div
                ref={section1MessageImageRef}
                className="col-span-6 col-start-1 mb-8 md:col-span-10 md:col-start-1 md:row-start-1 md:mb-16"
              >
                {getImageUrl(
                  Array.isArray(data.aboutPageIntroSectionHeadingImage)
                    ? data.aboutPageIntroSectionHeadingImage[0]
                    : data.aboutPageIntroSectionHeadingImage,
                ) && (
                  <img
                    src={
                      getImageUrl(
                        Array.isArray(data.aboutPageIntroSectionHeadingImage)
                          ? data.aboutPageIntroSectionHeadingImage[0]
                          : data.aboutPageIntroSectionHeadingImage,
                      )!
                    }
                    className="w-full h-auto object-cover rounded-lg"
                    alt="Heading"
                  />
                )}
              </div>

              {/* 2. Mobile Doodles (Row 2 Left) - FIXED: Now has a Ref */}
              <div
                ref={section1MobileDoodlesRef}
                className="col-span-3 col-start-1 mb-8 flex items-end md:hidden"
              >
                <div className="w-full flex flex-col gap-4 justify-end h-full">
                  {getImageUrl(data.aboutPageIntroSectionDoodleImage1) && (
                    <img
                      src={getImageUrl(data.aboutPageIntroSectionDoodleImage1)!}
                      className="w-full h-auto object-contain"
                      alt="Doodle 1"
                    />
                  )}
                  {getImageUrl(data.aboutPageIntroSectionDoodleImage2) && (
                    <div className="pl-8">
                      <img
                        src={
                          getImageUrl(data.aboutPageIntroSectionDoodleImage2)!
                        }
                        className="w-full h-auto object-contain"
                        alt="Doodle 2"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* 3. Desktop Doodles + Portrait - Stacked vertically */}
              <div
                ref={section1DoodlesDesktopRef}
                className="hidden md:flex md:flex-col md:col-span-3 md:col-start-9 md:row-start-2"
              >
                {/* Doodles */}
                <div className="w-full">
                  <div className="flex gap-4 w-full">
                    {getImageUrl(data.aboutPageIntroSectionDoodleImage3) && (
                      <div
                        ref={section1ArrowDoodleRef}
                        className="flex-[1] flex items-end"
                      >
                        <img
                          src={
                            getImageUrl(data.aboutPageIntroSectionDoodleImage3)!
                          }
                          className="w-full h-auto object-contain max-h-[100px]"
                          alt="Arrow"
                        />
                      </div>
                    )}
                    <div
                      ref={section1StewartDoodleRef}
                      className="flex-[4] flex flex-col gap-4"
                    >
                      {getImageUrl(data.aboutPageIntroSectionDoodleImage1) && (
                        <img
                          src={
                            getImageUrl(data.aboutPageIntroSectionDoodleImage1)!
                          }
                          className="w-full h-auto object-contain"
                          alt="Doodle 1"
                        />
                      )}
                      {getImageUrl(data.aboutPageIntroSectionDoodleImage2) && (
                        <div className="pl-8">
                          <img
                            src={
                              getImageUrl(
                                data.aboutPageIntroSectionDoodleImage2,
                              )!
                            }
                            className="w-full h-auto object-contain"
                            alt="Doodle 2"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Portrait - directly under doodles */}
                <div ref={section1PortraitRef} className="w-full mt-4">
                  {getImageUrl(data.aboutPageIntroSectionPortraitImage) && (
                    <img
                      src={
                        getImageUrl(data.aboutPageIntroSectionPortraitImage)!
                      }
                      className="w-full h-auto object-cover rounded-lg"
                      alt="Portrait"
                    />
                  )}
                </div>
              </div>

              {/* 4. Portrait (Mobile only) */}
              <div
                ref={section1MobilePortraitRef}
                className="col-span-3 col-start-4 mb-8 md:hidden"
              >
                {getImageUrl(data.aboutPageIntroSectionPortraitImage) && (
                  <img
                    src={getImageUrl(data.aboutPageIntroSectionPortraitImage)!}
                    className="w-full h-auto object-cover rounded-lg"
                    alt="Portrait"
                  />
                )}
              </div>

              {/* 4. Text (Row 3 Mobile, Row 2 Desktop) */}
              <div
                ref={section1TextRef}
                className="col-span-6 col-start-1 mb-12 md:col-span-5 md:col-start-3 md:row-start-2 md:mb-0"
              >
                {data.aboutPageIntroSectionHeading && (
                  <h2 className="text-4xl md:text-5xl text-white font-headings mb-8">
                    {data.aboutPageIntroSectionHeading}
                  </h2>
                )}
                <div
                  className="text-base md:text-lg text-white/90 [&_p]:mb-6"
                  style={{ lineHeight: "1.5" }}
                  dangerouslySetInnerHTML={{
                    __html: (data.aboutPageIntroSectionText as { html: string })
                      .html,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* --- LAYER 1: HERO SECTION (On Top) --- */}
        <div
          className="absolute top-0 left-0 right-0 flex w-full overflow-hidden pt-20 md:pt-32 justify-center px-4 md:px-8 lg:px-16 xl:px-24 z-50"
          style={{ height: "100vh" }}
          ref={barSectionContainerRef}
        >
          <div className="flex h-full items-stretch gap-[2px] sm:gap-[3px] md:gap-1 max-w-container w-full mx-auto">
            {/* Left Text */}
            <div className="flex flex-col justify-start items-end z-10 flex-shrink-0 w-1/3 md:w-auto">
              <h2 className="text-white text-5xl sm:text-6xl md:text-6xl lg:text-7xl uppercase leading-[0.85] font-headings text-right antialiased">
                {["About", "The", "Mus", "Eum"].map((word, i) => (
                  <span
                    key={i}
                    ref={(el) => {
                      textLinesRef.current[i] = el;
                    }}
                    className="block opacity-0 invisible"
                  >
                    {word}
                  </span>
                ))}
              </h2>
            </div>

            {/* MOBILE BARS */}
            <div className="flex md:hidden justify-between items-stretch h-full pb-20 flex-1">
              {Array.from({ length: TOTAL_BARS_MOBILE }).map((_, index) => (
                <div
                  key={`mobile-${index}`}
                  className="relative flex flex-col h-full flex-1 mx-[2px]"
                >
                  <div
                    ref={(el) => {
                      mobileBarsRef.current[index] = el;
                    }}
                    className={`w-full bg-[#0f0b15] ${index === SPECIAL_BAR_INDEX_MOBILE ? "h-[70%]" : "h-full"}`}
                    style={{
                      transform: "scaleY(0)",
                      transformOrigin: "top",
                      opacity: 0,
                    }}
                  />
                  {index === SPECIAL_BAR_INDEX_MOBILE &&
                    data.aboutPageBackgroundImage &&
                    (() => {
                      const imageUrl = getImageUrl(
                        data.aboutPageBackgroundImage,
                      );
                      return imageUrl ? (
                        <div
                          ref={mobileImageRef}
                          className="w-full h-[30%] overflow-hidden z-20 flex-shrink-0 relative"
                          style={{
                            clipPath: "inset(0% 0% 100% 0%)",
                            opacity: 0,
                            willChange: "transform",
                            backgroundColor: "#1e1733",
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt="Background"
                            className="w-full h-full object-cover object-center image-blend"
                          />
                        </div>
                      ) : null;
                    })()}
                </div>
              ))}
            </div>

            {/* DESKTOP BARS */}
            <div className="hidden md:flex justify-between items-stretch h-full pb-20 flex-1">
              {Array.from({ length: TOTAL_BARS }).map((_, index) => (
                <div
                  key={`desktop-${index}`}
                  className="relative flex flex-col h-full flex-1 mx-[2px] sm:mx-[3px] md:mx-1"
                >
                  <div
                    ref={(el) => {
                      desktopBarsRef.current[index] = el;
                    }}
                    className={`w-full bg-[#0f0b15] ${index === SPECIAL_BAR_INDEX ? "h-[70%]" : "h-full"}`}
                    style={{
                      transform: "scaleY(0)",
                      transformOrigin: "top",
                      opacity: 0,
                    }}
                  />
                  {index === SPECIAL_BAR_INDEX &&
                    data.aboutPageBackgroundImage &&
                    (() => {
                      const imageUrl = getImageUrl(
                        data.aboutPageBackgroundImage,
                      );
                      return imageUrl ? (
                        <div
                          ref={desktopImageRef}
                          className="w-full h-[30%] overflow-hidden z-20 flex-shrink-0 relative"
                          style={{
                            clipPath: "inset(0% 0% 100% 0%)",
                            opacity: 0,
                            willChange: "transform",
                            backgroundColor: "#1e1733",
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt="Background"
                            className="w-full h-full object-cover object-center image-blend"
                          />
                        </div>
                      ) : null;
                    })()}
                </div>
              ))}
            </div>
          </div>

          {/* Counter Overlay */}
          <div
            ref={counterRef}
            className="fixed top-0 left-0 w-full flex flex-col items-center justify-center pointer-events-none z-[150]"
            style={{ opacity: 0, visibility: "hidden", height: "100vh" }}
          >
            <div className="w-[80vw] md:w-[60vw] flex flex-col items-center">
              <div className="w-full flex items-center justify-end leading-[0.75] gap-0">
                <div
                  ref={counterHundredsRef}
                  className="text-white font-black leading-[0.75] flex-shrink-0 mix-blend-difference text-right font-headings-uppercase"
                  style={{
                    fontWeight: 900,
                    width: "33.333%",
                    fontSize: isMobileView ? "70vw" : "50vw",
                  }}
                ></div>
                <div
                  ref={counterTensRef}
                  className="text-white font-black leading-[0.75] flex-shrink-0 mix-blend-multiply text-right font-headings-uppercase"
                  style={{
                    fontWeight: 900,
                    width: "33.333%",
                    fontSize: isMobileView ? "70vw" : "50vw",
                  }}
                ></div>
                <div
                  ref={counterOnesRef}
                  className="text-white font-black leading-[0.75] flex-shrink-0 mix-blend-multiply text-right font-headings-uppercase"
                  style={{
                    fontWeight: 900,
                    width: "33.333%",
                    fontSize: isMobileView ? "70vw" : "50vw",
                  }}
                >
                  0
                </div>
              </div>
              <div className="w-full -mt-[0.15em]">
                <FittedText
                  text="MICROPHONES"
                  className="font-black text-white mix-blend-multiply uppercase"
                  lineHeight={1}
                  scaleY={1}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- REST OF PAGE --- */}
      {/* Sound Recording */}
      {data.aboutPageSoundRecordingSectionHeading && (
        <div className="w-full py-32">
          <div className="about-grid-container relative">
            <div
              ref={soundRecordingDoodleRef}
              className="col-span-6 col-start-1 row-start-2 mb-8 md:mb-0 md:col-span-9 md:col-start-1 md:row-start-1 flex items-center justify-start relative z-0"
            >
              {getImageUrl(data.aboutPageSoundRecordingSectionDoodleImage) && (
                <img
                  src={
                    getImageUrl(data.aboutPageSoundRecordingSectionDoodleImage)!
                  }
                  className="w-full h-auto object-contain"
                  alt="Doodle"
                />
              )}
            </div>
            <div
              ref={soundRecordingHeadingRef}
              className="col-span-4 col-start-2 row-start-1 md:col-span-6 md:col-start-7 md:row-start-1 flex flex-col items-start justify-start md:pt-8 relative z-10"
            >
              <div
                style={{
                  transform: "rotate(-8deg)",
                  transformOrigin: "center right",
                }}
              >
                <div
                  className="leading-[0.75] text-right font-headings"
                >
                  <div className="text-white text-[3rem] md:text-[4rem] lg:text-[5rem]">
                    sound
                  </div>
                  <div className="text-[#714ED0] text-[5rem] md:text-[7rem] lg:text-[9rem]">
                    recordings
                  </div>
                </div>
              </div>
            </div>
            {data.aboutPageSoundRecordingSectionText && (
              <div
                ref={soundRecordingTextRef}
                className="col-span-4 col-start-2 row-start-3 mt-4 md:col-span-6 md:col-start-7 md:row-start-2 md:self-start md:mt-8 relative z-10"
              >
                <div className="md:w-2/3">
                  <div
                    className="text-base md:text-lg text-white/90 [&_p]:mb-3 text-right [&_p]:text-right"
                    style={{ lineHeight: "1.5" }}
                    dangerouslySetInnerHTML={{
                      __html: (
                        data.aboutPageSoundRecordingSectionText as {
                          html: string;
                        }
                      ).html,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Other Sections */}
      <div className="w-full py-32">
        <div className="about-grid-container">
          {[
            {
              key: "microphoneSweep",
              heading: data.aboutPageMicrophoneSweepSectionHeadingText,
              text: data.aboutPageMicrophoneSweepSectionText,
              isScarcity: false,
            },
            {
              key: "scarcity",
              heading: data.aboutPageScarcitySectionHeading,
              text: data.aboutPageScarcitySectionText,
              isScarcity: true,
            },
            {
              key: "otherSites",
              heading: data.aboutPageOtherSitesSectionHeading,
              text: data.aboutPageOtherSitesSectionText,
              isScarcity: false,
            },
            {
              key: "acknowledgements",
              heading: data.aboutPageAcknowledgementsSectionHeading,
              text: data.aboutPageAcknowledgementsSectionText,
              isScarcity: false,
            },
          ].map((section) => {
            if (!section.heading) return null;

            if (section.isScarcity) {
              return (
                <React.Fragment key={section.key}>
                  {/* Scale Image Container - moved above heading */}
                  {getImageUrl(data.aboutPageScarcitySectionScaleImage1) && (
                    <div className="col-span-4 md:col-span-4 md:col-start-1 mb-8 relative h-fit">
                      <img
                        src={
                          getImageUrl(data.aboutPageScarcitySectionScaleImage1)!
                        }
                        className="w-full h-auto object-contain"
                        alt="Scale"
                      />
                      {/* Needle positioned over the scale */}
                      {getImageUrl(
                        data.aboutPageScarcitySectionScaleImage2,
                      ) && (
                        <div
                          ref={needleRef}
                          className="absolute bottom-10 left-5/8 w-[30%]"
                          style={{
                            transformOrigin: "90% 80%",
                          }}
                        >
                          <img
                            src={
                              getImageUrl(
                                data.aboutPageScarcitySectionScaleImage2,
                              )!
                            }
                            className="w-full h-auto object-contain"
                            alt="Needle"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="scarcity-section w-full mb-16 flex flex-col items-start justify-start col-span-6 col-start-1 md:col-span-12 md:col-start-1">
                    <h2 className="font-headings uppercase w-full antialiased">
                      <FittedText
                        text="SCARCITY"
                        className="font-black text-white antialiased"
                        lineHeight={0.7}
                        scaleY={1}
                      />
                      <FittedText
                        text="SCALE"
                        className="font-black leading-[-2rem] text-[#714ED0] antialiased"
                        lineHeight={0.75}
                        scaleY={0.5}
                        style={{ marginTop: "-2%" }}
                      />
                    </h2>
                  </div>
                  {section.text && (
                    <div
                      className="text-white/90 text-base md:text-lg mb-8 col-span-6 col-start-1 md:col-span-12 md:col-start-1"
                      style={{ lineHeight: "1.5" }}
                      dangerouslySetInnerHTML={{
                        __html: (section.text as { html: string }).html,
                      }}
                    />
                  )}

                  {/* Mic Doodle moved to old scale position */}
                  {getImageUrl(data.aboutPageScarcitySectionDoodleImage) && (
                    <div className="col-span-3 col-start-1 mb-8 md:col-span-4 md:col-start-1 md:mb-0 mt-20">
                      <img
                        src={
                          getImageUrl(data.aboutPageScarcitySectionDoodleImage)!
                        }
                        className="w-full h-auto object-contain"
                        alt="Doodle"
                      />
                    </div>
                  )}

                  {data.scarcityLevels && data.scarcityLevels.length > 0 && (
                    <div className="flex flex-col gap-0 mt-20 w-full col-span-6 md:col-span-7 md:col-start-6">
                      {data.scarcityLevels.map((level, idx) => {
                        const isEven = idx % 2 !== 0;
                        // Alternate between +3 and -3 degrees for slant (matching rotation)
                        const slantDegrees = isEven ? 3 : -3;
                        return (
                          <div
                            key={level.scarcityNumber}
                            className={`scarcity-item relative flex ${isEven ? "flex-row-reverse" : "flex-row"} items-center gap-1 md:gap-3 mb-4 md:mb-8`}
                          >
                            <div
                              className="relative z-20 text-[#8b64e8] leading-[0.75] select-none flex-shrink-0 font-headings-uppercase"
                              style={{
                                fontSize: "clamp(8rem, 30vw, 20rem)",
                                fontWeight: 900,
                              }}
                            >
                              {level.scarcityNumber}
                            </div>
                            <div className="relative z-10 flex-1 w-full flex justify-start">
                              <div
                                className={`bg-black/90 p-4 md:p-5 border border-white/10 relative min-h-[100px] w-full flex flex-col justify-center ${isEven ? "text-right" : ""}`}
                                style={{
                                  transform: `rotate(${slantDegrees}deg)`,
                                  transformOrigin: "center",
                                }}
                              >
                                <h3 className="text-3xl md:text-4xl font-black text-[#8b64e8] font-headings uppercase mb-2 tracking-tight leading-[0.9]">
                                  {level.scarcityLabel}
                                </h3>
                                <p
                                  className="text-white/90 text-base md:text-lg"
                                  style={{ lineHeight: "1.5" }}
                                >
                                  {level.scarcityDescription}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Unicorn Doodle after scarcity items - last 2 columns */}
                  {getImageUrl(data.aboutPageScarcitySectionUnicornDoodle) && (
                    <div className="col-span-3 col-start-4 mt-12 md:col-span-3 md:col-start-9 md:mt-0">
                      <img
                        src={
                          getImageUrl(
                            data.aboutPageScarcitySectionUnicornDoodle,
                          )!
                        }
                        className="w-full h-auto object-contain"
                        alt="Unicorn Doodle"
                      />
                    </div>
                  )}

                  <div className="col-span-6 md:col-span-12 h-32 md:h-48"></div>
                </React.Fragment>
              );
            } else if (section.key === "microphoneSweep") {
              return (
                <React.Fragment key={section.key}>
                  <div
                    ref={sweepHeadingRef}
                    className="col-span-5 col-start-1 row-start-1 mb-8 pt-8 md:col-span-6 md:col-start-1 md:row-start-1 md:mb-0 relative z-10 md:pt-24"
                  >
                    <h2 className="font-headings lowercase leading-[1] text-right">
                      <div className="text-[#714ED0] text-[3rem] md:text-[3rem] lg:text-[4rem]">
                        {section.heading &&
                          (section.heading as string)
                            .toLowerCase()
                            .split(" ")
                            .slice(0, 2)
                            .join(" ")}
                      </div>
                      <div className="text-white text-[3.5rem] md:text-[3rem] lg:text-[4rem] -mt-2">
                        {section.heading &&
                          (section.heading as string)
                            .toLowerCase()
                            .split(" ")
                            .slice(2)
                            .join(" ")}
                      </div>
                    </h2>
                  </div>
                  {getImageUrl(
                    data.aboutPageMicrophoneSweepSectionDoodleImage,
                  ) && (
                    <div
                      ref={sweepDoodleRef}
                      className="col-span-6 col-start-1 row-start-1 mb-8 md:col-span-4 md:col-start-6 md:row-start-1 md:mb-0 flex items-start relative z-0"
                    >
                      <img
                        src={
                          getImageUrl(
                            data.aboutPageMicrophoneSweepSectionDoodleImage,
                          )!
                        }
                        className="w-full h-auto object-cover"
                        alt="Studio"
                      />
                    </div>
                  )}
                  {section.text && (
                    <div
                      ref={sweepBodyRef}
                      className="col-span-4 col-start-3 row-start-1 mt-4 self-end pb-12 md:col-span-3 md:col-start-9 md:row-start-1 md:mt-0 md:self-end md:pb-20 relative z-10"
                    >
                      <div
                        className="text-base md:text-lg text-white/90 [&_p]:mb-3"
                        style={{ lineHeight: "1.5" }}
                        dangerouslySetInnerHTML={{
                          __html: (section.text as { html: string }).html,
                        }}
                      />
                    </div>
                  )}
                  <div className="col-span-6 md:col-span-12 h-32 md:h-48"></div>
                </React.Fragment>
              );
            } else {
              const isOtherSites = section.key === "otherSites";
              const isAcknowledgements = section.key === "acknowledgements";
              return (
                <React.Fragment key={section.key}>
                  <div
                    className={`about-section mb-20 col-span-6 col-start-1 ${
                      isOtherSites
                        ? "md:col-span-6 md:col-start-2"
                        : isAcknowledgements
                          ? "md:col-span-8 md:col-start-5"
                          : "max-w-3xl md:col-span-12"
                    }`}
                  >
                    {isOtherSites ? (
                      <h2 className="text-6xl font-headings mb-4 md:text-right">
                        {(() => {
                          const heading = section.heading as string;
                          if (
                            heading
                              .toLowerCase()
                              .includes("notable collections")
                          ) {
                            // Split by "and" to separate the two lines
                            const parts = heading.split(/\s+and\s+/i);
                            return (
                              <>
                                <div className="mb-4">
                                  <span className="text-white inline">
                                    Other{" "}
                                  </span>
                                  <span className="text-[#714ED0] inline">
                                    notable collections
                                  </span>
                                </div>
                                <div>
                                  <span className="text-white">
                                    and {parts[1]}
                                  </span>
                                </div>
                              </>
                            );
                          }
                          return (
                            <span className="text-[#714ED0]">{heading}</span>
                          );
                        })()}
                      </h2>
                    ) : (
                      <h2 className="text-4xl text-white font-headings mb-4 leading-2">
                        {section.heading as string}
                      </h2>
                    )}
                    {section.text && (
                      <div
                        className={`text-base md:text-lg text-white/90 [&_a]:text-[#714ED0] [&_a]:block [&_a]:mt-9 [&_a]:mb-1 ${isOtherSites ? "md:text-right" : ""}`}
                        style={{ lineHeight: "1.5" }}
                        dangerouslySetInnerHTML={{
                          __html: (section.text as { html: string }).html,
                        }}
                      />
                    )}
                  </div>
                  {isOtherSites && (
                    <div className="col-span-6 md:col-span-12 h-64 md:h-96"></div>
                  )}
                  {/* Final doodle below acknowledgements - columns 5-8 */}
                  {isAcknowledgements &&
                    getImageUrl(data.aboutPageAdditionalDoodleImage1) && (
                      <div className="col-span-4 col-start-1 mt-8 md:col-span-3 md:col-start-5">
                        <img
                          src={
                            getImageUrl(data.aboutPageAdditionalDoodleImage1)!
                          }
                          className="w-full h-auto object-contain"
                          alt="Final Doodle"
                        />
                      </div>
                    )}
                </React.Fragment>
              );
            }
          })}
        </div>
      </div>

      {showScrollPrompt && (
        <ScrollPrompt onHide={() => setShowScrollPrompt(false)} />
      )}

      <Footer extinctLogo={extinctLogoUrl} />
    </main>
  );
}
